/**
 * Offline queue for write requests (POST/PUT/DELETE) using IndexedDB.
 * Queues requests when offline and replays them when back online.
 * Blacklists endpoints that involve file uploads (FormData) since they
 * cannot be reliably serialized.
 */

const DB_NAME = 'karry-offline';
const STORE_NAME = 'queue';
const DB_VERSION = 1;

let dbInstance = null;

function openDB() {
    if (dbInstance) return Promise.resolve(dbInstance);
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
            }
        };
        req.onsuccess = (e) => {
            dbInstance = e.target.result;
            resolve(dbInstance);
        };
        req.onerror = (e) => reject(e.target.error);
    });
}

async function tx(mode = 'readonly') {
    const db = await openDB();
    return db.transaction(STORE_NAME, mode).objectStore(STORE_NAME);
}

export async function enqueue(item) {
    const store = await tx('readwrite');
    return new Promise((resolve, reject) => {
        const r = store.add({ ...item, ts: Date.now() });
        r.onsuccess = () => resolve(r.result);
        r.onerror = (e) => reject(e.target.error);
    });
}

export async function getAll() {
    const store = await tx();
    return new Promise((resolve, reject) => {
        const r = store.getAll();
        r.onsuccess = () => resolve(r.result || []);
        r.onerror = (e) => reject(e.target.error);
    });
}

export async function remove(id) {
    const store = await tx('readwrite');
    return new Promise((resolve, reject) => {
        const r = store.delete(id);
        r.onsuccess = () => resolve();
        r.onerror = (e) => reject(e.target.error);
    });
}

export async function count() {
    const store = await tx();
    return new Promise((resolve, reject) => {
        const r = store.count();
        r.onsuccess = () => resolve(r.result);
        r.onerror = (e) => reject(e.target.error);
    });
}

export async function clear() {
    const store = await tx('readwrite');
    return new Promise((resolve, reject) => {
        const r = store.clear();
        r.onsuccess = () => resolve();
        r.onerror = (e) => reject(e.target.error);
    });
}

/**
 * Replays all queued requests using axios. Returns counts {ok, failed, conflict}.
 * Behavior:
 *  - 4xx errors: drop the item (server validation rejected it permanently)
 *  - Network errors / 5xx: increment retry counter. If retries >= MAX_RETRIES,
 *    drop and report as conflict. Otherwise keep for next attempt.
 *  - We DO NOT break the loop on network errors so a single stuck item
 *    doesn't block the rest of the queue.
 */
const MAX_RETRIES = 5;

async function updateRetries(id, retries) {
    const store = await tx('readwrite');
    return new Promise((resolve) => {
        const r = store.get(id);
        r.onsuccess = () => {
            const it = r.result;
            if (!it) return resolve();
            it.retries = retries;
            it.lastError = String(it.lastError || '').slice(0, 200);
            const u = store.put(it);
            u.onsuccess = () => resolve();
            u.onerror = () => resolve();
        };
        r.onerror = () => resolve();
    });
}

export async function flushQueue(axios) {
    const items = await getAll();
    const results = { ok: 0, failed: 0, conflict: 0, dropped: 0 };
    for (const item of items) {
        try {
            await axios({
                method: item.method,
                url: item.url,
                data: item.data,
                headers: item.headers || {},
                timeout: 25000  // 25s para que no se cuelgue
            });
            await remove(item.id);
            results.ok += 1;
        } catch (err) {
            const status = err?.response?.status;
            const isClient4xx = status && status >= 400 && status < 500;
            if (isClient4xx) {
                // Permanent error: drop the item
                await remove(item.id);
                results.conflict += 1;
                console.warn('[OfflineQueue] dropped 4xx:', item.method, item.url, status, err?.response?.data);
            } else {
                const retries = (item.retries || 0) + 1;
                if (retries >= MAX_RETRIES) {
                    await remove(item.id);
                    results.dropped += 1;
                    console.warn('[OfflineQueue] dropped after retries:', item.method, item.url, err?.message);
                } else {
                    await updateRetries(item.id, retries);
                    results.failed += 1;
                    // No break: continue trying the rest of the queue
                }
            }
        }
    }
    return results;
}
